import { Building2, Users, BarChart3, MessageSquare, Plus, ArrowRight } from 'lucide-react'
import { TenantSummary } from '../../../services/api'

interface Props {
  tenants: TenantSummary[]
  onNewCompany: () => void
  onSelectCompany: (tenantId: string) => void
}

export default function AdminOverview({ tenants, onNewCompany, onSelectCompany }: Props) {
  const totalUsers = tenants.reduce(
    (sum, t) => sum + Object.keys(t.user_roles || {}).length + 1,
    0
  )
  const activeCompanies = tenants.filter(t => t.status === 'active').length
  const totalMessages = tenants.reduce(
    (sum, t) => sum + (t.usage?.messages_used || 0),
    0
  )

  const recentCompanies = [...tenants]
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .slice(0, 5)

  const stats = [
    { label: 'Total Companies', value: tenants.length, icon: Building2, color: 'purple' },
    { label: 'Total Users', value: totalUsers, icon: Users, color: 'blue' },
    { label: 'Active Companies', value: activeCompanies, icon: BarChart3, color: 'green' },
    { label: 'Messages Used', value: totalMessages.toLocaleString(), icon: MessageSquare, color: 'amber' },
  ]

  const colorMap: Record<string, { bg: string; icon: string }> = {
    purple: { bg: 'bg-purple-100', icon: 'text-purple-600' },
    blue: { bg: 'bg-blue-100', icon: 'text-blue-600' },
    green: { bg: 'bg-green-100', icon: 'text-green-600' },
    amber: { bg: 'bg-amber-100', icon: 'text-amber-600' },
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const colors = colorMap[stat.color]
          return (
            <div
              key={stat.label}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${colors.icon}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Recent Companies + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Companies */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Recent Companies</h3>
          </div>
          {recentCompanies.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-gray-400">
              No companies yet. Create your first company to get started.
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentCompanies.map((t) => (
                <button
                  key={t.tenant_id}
                  onClick={() => onSelectCompany(t.tenant_id)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                      <p className="text-xs text-gray-400 truncate">{t.admin_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-400">
                      {Object.keys(t.user_roles || {}).length + 1} users
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button
              onClick={onNewCompany}
              className="w-full flex items-center gap-3 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">New Company</span>
            </button>
          </div>

          {/* Plan Distribution */}
          <div className="mt-6">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Plans</h4>
            {['free', 'starter', 'pro', 'enterprise'].map((plan) => {
              const count = tenants.filter(t => t.plan === plan).length
              if (count === 0) return null
              return (
                <div key={plan} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-gray-600 capitalize">{plan}</span>
                  <span className="text-sm font-medium text-gray-900">{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
